import { create } from 'zustand';
import { caService } from '../services/caService';
import {
  Assignment,
  AuditLogEvent,
  CAClientDetail,
  CAClientListItem,
  CAGlobalFilters,
  CAStaffMember,
  ClientStatus,
  CADashboardAnalytics,
  DocumentFolderNode,
  DocumentRequest,
  DocumentWorkflowRequirement,
  EngagementStage,
  Task,
} from '../contracts/ca.types';
import { asCAServiceError, isFeatureUnavailableError } from '../services/serviceErrors';

type LoadingKey =
  | 'clients'
  | 'clientDetail'
  | 'tasks'
  | 'documents'
  | 'documentFolders'
  | 'documentWorkflow'
  | 'assignments'
  | 'auditLog'
  | 'analytics'
  | 'createTask'
  | 'inviteClient'
  | 'revokeClientInvite'
  | 'updateStage'
  | 'updateAssignment'
  | 'documentLifecycle'
  | 'createDocumentRequest'
  | 'uploadDocument'
  | 'createClient'
  | 'staff';
const CA_ACTIVE_FIRM_ID_KEY = 'ca_active_firm_id';
const CA_SELECTED_CLIENT_ORG_ID_KEY = 'ca_selected_client_org_id';
const CA_SELECTED_MAPPING_ID_KEY = 'ca_selected_mapping_id';

const getInitialFirmId = (): string => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(CA_ACTIVE_FIRM_ID_KEY) || '';
};

const readStoredFirmId = (): string => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(CA_ACTIVE_FIRM_ID_KEY)?.trim() || '';
};

const readStoredSelectedClientOrgId = (): string | null => {
  if (typeof window === 'undefined') return null;
  const value = localStorage.getItem(CA_SELECTED_CLIENT_ORG_ID_KEY)?.trim() || '';
  return value || null;
};

const readStoredSelectedMappingId = (): string | null => {
  if (typeof window === 'undefined') return null;
  const value = localStorage.getItem(CA_SELECTED_MAPPING_ID_KEY)?.trim() || '';
  return value || null;
};

/** Prefer in-memory firm id; fall back to localStorage (set on login) and sync the store. */
const resolveFirmId = (get: () => CAWorkspaceState, set: (partial: Partial<CAWorkspaceState>) => void): string => {
  let id = get().activeFirmId?.trim() || '';
  if (id) return id;
  id = readStoredFirmId();
  if (id) set({ activeFirmId: id });
  return id;
};

interface CAWorkspaceState {
  activeFirmId: string;
  selectedClientOrgId: string | null;
  selectedMappingId: string | null;
  selectedClientId: string | null;
  clientStatus: Record<string, ClientStatus>;
  globalFilters: CAGlobalFilters;
  clients: CAClientListItem[];
  staff: CAStaffMember[];
  clientDetailsByOrgId: Record<string, CAClientDetail>;
  tasksByOrgId: Record<string, Task[]>;
  assignmentsByOrgId: Record<string, Assignment[]>;
  documentsByOrgId: Record<string, DocumentRequest[]>;
  documentFoldersByOrgId: Record<string, DocumentFolderNode[]>;
  documentWorkflowByOrgId: Record<string, DocumentWorkflowRequirement[]>;
  auditLogByOrgId: Record<string, AuditLogEvent[]>;
  analytics: CADashboardAnalytics | null;
  loading: Record<LoadingKey, boolean>;
  errors: Record<LoadingKey, string | undefined>;
  setFirmId: (firmId: string) => void;
  setGlobalFilters: (filters: Partial<CAGlobalFilters>) => void;
  selectClient: (clientOrgId: string, mappingId: string) => Promise<void>;
  loadClients: () => Promise<void>;
  loadClientDetails: (clientOrgId: string, mappingId: string) => Promise<void>;
  loadTasks: (clientOrgId: string, mappingId: string) => Promise<void>;
  loadDocuments: (clientOrgId: string, mappingId: string) => Promise<void>;
  loadDocumentFolders: (clientOrgId: string, mappingId: string) => Promise<void>;
  loadDocumentWorkflow: (clientOrgId: string, mappingId: string) => Promise<void>;
  loadAssignments: (clientOrgId: string, mappingId: string) => Promise<void>;
  loadAuditLog: (clientOrgId: string, mappingId: string) => Promise<void>;
  loadAnalytics: () => Promise<void>;
  createTask: (payload: {
    clientOrganizationId?: string;
    mappingId?: string;
    title: string;
    description?: string;
    dueDate?: string;
    priority?: 'low' | 'medium' | 'high' | 'critical';
    assigneeUserId?: string;
    ownerSide?: 'client' | 'ca';
  }) => Promise<void>;
  createDocumentRequest: (payload: {
    title: string;
    dueDate?: string;
    notes?: string;
  }) => Promise<void>;
  uploadDocument: (payload: {
    documentRequestId: string;
    fileName: string;
    contentType: string;
  }) => Promise<void>;
  updateClientStage: (payload: { stage: EngagementStage; pitchNotes?: string }) => Promise<void>;
  updateAssignment: (payload: { assignmentId: string; assigneeUserId?: string; dueDate?: string; dependencyTaskIds?: string[] }) => Promise<void>;
  updateDocumentLifecycle: (payload: {
    documentRequestId: string;
    status: 'requested' | 'uploaded' | 'verified' | 'rejected';
  }) => Promise<void>;
  bulkDocumentAction: (payload: {
    action: 'bulk_request' | 'bulk_reminder' | 'bulk_status_update';
    documentIds: string[];
    status?: 'requested' | 'uploaded' | 'verified' | 'rejected';
  }) => Promise<void>;
  inviteClient: (payload: {
    email: string;
    organizationName?: string;
    accessLevel: 'read' | 'review' | 'operate';
    inviteTarget?: 'business_owner' | 'client';
  }) => Promise<void>;
  revokeClientInvite: (inviteId: string) => Promise<void>;
  createClient: (payload: {
    businessName: string;
    gstin?: string;
    email?: string;
    phone?: string;
  }) => Promise<void>;
  loadStaff: () => Promise<void>;
  createStaff: (payload: {
    fullName: string;
    email: string;
    phone?: string;
    role: 'manager' | 'analyst' | 'support';
  }) => Promise<void>;
}

const initialLoading: Record<LoadingKey, boolean> = {
  clients: false,
  clientDetail: false,
  tasks: false,
  documents: false,
  documentFolders: false,
  documentWorkflow: false,
  assignments: false,
  auditLog: false,
  analytics: false,
  createTask: false,
  inviteClient: false,
  revokeClientInvite: false,
  updateStage: false,
  updateAssignment: false,
  documentLifecycle: false,
  createDocumentRequest: false,
  uploadDocument: false,
  createClient: false,
  staff: false,
};

const initialErrors: Record<LoadingKey, string | undefined> = {
  clients: undefined,
  clientDetail: undefined,
  tasks: undefined,
  documents: undefined,
  documentFolders: undefined,
  documentWorkflow: undefined,
  assignments: undefined,
  auditLog: undefined,
  analytics: undefined,
  createTask: undefined,
  inviteClient: undefined,
  revokeClientInvite: undefined,
  updateStage: undefined,
  updateAssignment: undefined,
  documentLifecycle: undefined,
  createDocumentRequest: undefined,
  uploadDocument: undefined,
  createClient: undefined,
  staff: undefined,
};

export const useCAWorkspaceStore = create<CAWorkspaceState>((set, get) => ({
  activeFirmId: getInitialFirmId(),
  selectedClientOrgId: readStoredSelectedClientOrgId(),
  selectedMappingId: readStoredSelectedMappingId(),
  selectedClientId: readStoredSelectedClientOrgId(),
  clientStatus: {},
  globalFilters: {},
  clients: [],
  staff: [],
  clientDetailsByOrgId: {},
  tasksByOrgId: {},
  assignmentsByOrgId: {},
  documentsByOrgId: {},
  documentFoldersByOrgId: {},
  documentWorkflowByOrgId: {},
  auditLogByOrgId: {},
  analytics: null,
  loading: initialLoading,
  errors: initialErrors,

  setFirmId: (firmId) => {
    if (typeof window !== 'undefined') {
      if (firmId) {
        localStorage.setItem(CA_ACTIVE_FIRM_ID_KEY, firmId);
      } else {
        localStorage.removeItem(CA_ACTIVE_FIRM_ID_KEY);
      }
    }
    set({ activeFirmId: firmId });
  },
  setGlobalFilters: (filters) => {
    set((state) => ({ globalFilters: { ...state.globalFilters, ...filters } }));
  },

  loadClients: async () => {
    set((state) => ({ loading: { ...state.loading, clients: true }, errors: { ...state.errors, clients: undefined } }));
    try {
      const firmId = resolveFirmId(get, set);
      if (!firmId) {
        set((state) => ({
          loading: { ...state.loading, clients: false },
          errors: { ...state.errors, clients: 'CA firm context is missing. Re-login or switch workspace.' },
        }));
        return;
      }
      const response = await caService.getCAClients({ firmId });
      const first = response.items[0];
      const selectedClientFromState = get().selectedClientOrgId;
      const selectedMappingFromState = get().selectedMappingId;
      const matchedClient = response.items.find((item) => item.organization.id === selectedClientFromState);
      const nextSelectedClientOrgId = matchedClient?.organization.id ?? first?.organization.id ?? null;
      const nextSelectedMappingId = matchedClient?.mapping.id ?? selectedMappingFromState ?? first?.mapping.id ?? null;
      if (typeof window !== 'undefined') {
        if (nextSelectedClientOrgId) {
          localStorage.setItem(CA_SELECTED_CLIENT_ORG_ID_KEY, nextSelectedClientOrgId);
        } else {
          localStorage.removeItem(CA_SELECTED_CLIENT_ORG_ID_KEY);
        }
        if (nextSelectedMappingId) {
          localStorage.setItem(CA_SELECTED_MAPPING_ID_KEY, nextSelectedMappingId);
        } else {
          localStorage.removeItem(CA_SELECTED_MAPPING_ID_KEY);
        }
      }
      set((state) => ({
        clients: response.items,
        clientStatus: response.items.reduce<Record<string, ClientStatus>>((acc, client) => {
          acc[client.organization.id] = client.clientStatus ?? 'LEAD';
          return acc;
        }, {}),
        selectedClientOrgId: nextSelectedClientOrgId,
        selectedClientId: nextSelectedClientOrgId,
        selectedMappingId: nextSelectedMappingId,
        loading: { ...state.loading, clients: false },
      }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, clients: false },
        errors: { ...state.errors, clients: serviceError.message },
      }));
    }
  },

  selectClient: async (clientOrgId, mappingId) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(CA_SELECTED_CLIENT_ORG_ID_KEY, clientOrgId);
      localStorage.setItem(CA_SELECTED_MAPPING_ID_KEY, mappingId);
    }
    set({ selectedClientOrgId: clientOrgId, selectedMappingId: mappingId });
    set({ selectedClientId: clientOrgId });
    await Promise.all([
      get().loadClientDetails(clientOrgId, mappingId),
      get().loadTasks(clientOrgId, mappingId),
      get().loadDocuments(clientOrgId, mappingId),
      get().loadDocumentFolders(clientOrgId, mappingId),
      get().loadDocumentWorkflow(clientOrgId, mappingId),
      get().loadAssignments(clientOrgId, mappingId),
      get().loadAuditLog(clientOrgId, mappingId),
    ]);
  },

  loadClientDetails: async (clientOrgId, mappingId) => {
    set((state) => ({ loading: { ...state.loading, clientDetail: true }, errors: { ...state.errors, clientDetail: undefined } }));
    try {
      const details = await caService.getClientDetails({ clientOrganizationId: clientOrgId, mappingId });
      set((state) => ({
        clientDetailsByOrgId: { ...state.clientDetailsByOrgId, [clientOrgId]: details },
        loading: { ...state.loading, clientDetail: false },
      }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, clientDetail: false },
        errors: { ...state.errors, clientDetail: serviceError.message },
      }));
    }
  },

  loadTasks: async (clientOrgId, mappingId) => {
    set((state) => ({ loading: { ...state.loading, tasks: true }, errors: { ...state.errors, tasks: undefined } }));
    try {
      const response = await caService.getTasks({ clientOrganizationId: clientOrgId, mappingId });
      set((state) => ({
        tasksByOrgId: { ...state.tasksByOrgId, [clientOrgId]: response.items },
        loading: { ...state.loading, tasks: false },
      }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, tasks: false },
        errors: { ...state.errors, tasks: serviceError.message },
      }));
    }
  },

  loadDocuments: async (clientOrgId, mappingId) => {
    set((state) => ({ loading: { ...state.loading, documents: true }, errors: { ...state.errors, documents: undefined } }));
    try {
      const response = await caService.getDocumentRequests({ clientOrganizationId: clientOrgId, mappingId });
      set((state) => ({
        documentsByOrgId: { ...state.documentsByOrgId, [clientOrgId]: response.items },
        loading: { ...state.loading, documents: false },
      }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, documents: false },
        errors: { ...state.errors, documents: serviceError.message },
      }));
    }
  },

  loadDocumentFolders: async (clientOrgId, mappingId) => {
    set((state) => ({ loading: { ...state.loading, documentFolders: true }, errors: { ...state.errors, documentFolders: undefined } }));
    try {
      const response = await caService.getDocumentFolders({ clientOrganizationId: clientOrgId, mappingId });
      set((state) => ({
        documentFoldersByOrgId: { ...state.documentFoldersByOrgId, [clientOrgId]: response.items },
        loading: { ...state.loading, documentFolders: false },
      }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          documentFoldersByOrgId: { ...state.documentFoldersByOrgId, [clientOrgId]: [] },
          loading: { ...state.loading, documentFolders: false },
          errors: { ...state.errors, documentFolders: undefined },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, documentFolders: false },
        errors: { ...state.errors, documentFolders: serviceError.message },
      }));
    }
  },

  loadDocumentWorkflow: async (clientOrgId, mappingId) => {
    set((state) => ({ loading: { ...state.loading, documentWorkflow: true }, errors: { ...state.errors, documentWorkflow: undefined } }));
    try {
      const response = await caService.getDocumentWorkflow({ clientOrganizationId: clientOrgId, mappingId });
      set((state) => ({
        documentWorkflowByOrgId: { ...state.documentWorkflowByOrgId, [clientOrgId]: response.items },
        loading: { ...state.loading, documentWorkflow: false },
      }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          documentWorkflowByOrgId: { ...state.documentWorkflowByOrgId, [clientOrgId]: [] },
          loading: { ...state.loading, documentWorkflow: false },
          errors: { ...state.errors, documentWorkflow: undefined },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, documentWorkflow: false },
        errors: { ...state.errors, documentWorkflow: serviceError.message },
      }));
    }
  },

  loadAssignments: async (clientOrgId, mappingId) => {
    set((state) => ({ loading: { ...state.loading, assignments: true }, errors: { ...state.errors, assignments: undefined } }));
    try {
      const response = await caService.getAssignments({ clientOrganizationId: clientOrgId, mappingId });
      set((state) => ({
        assignmentsByOrgId: { ...state.assignmentsByOrgId, [clientOrgId]: response.items },
        loading: { ...state.loading, assignments: false },
      }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          assignmentsByOrgId: { ...state.assignmentsByOrgId, [clientOrgId]: [] },
          loading: { ...state.loading, assignments: false },
          errors: { ...state.errors, assignments: undefined },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, assignments: false },
        errors: { ...state.errors, assignments: serviceError.message },
      }));
    }
  },

  loadAuditLog: async (clientOrgId, mappingId) => {
    set((state) => ({ loading: { ...state.loading, auditLog: true }, errors: { ...state.errors, auditLog: undefined } }));
    try {
      const response = await caService.getAuditLog({ clientOrganizationId: clientOrgId, mappingId });
      set((state) => ({
        auditLogByOrgId: { ...state.auditLogByOrgId, [clientOrgId]: response.items },
        loading: { ...state.loading, auditLog: false },
      }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          auditLogByOrgId: { ...state.auditLogByOrgId, [clientOrgId]: [] },
          loading: { ...state.loading, auditLog: false },
          errors: { ...state.errors, auditLog: undefined },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, auditLog: false },
        errors: { ...state.errors, auditLog: serviceError.message },
      }));
    }
  },

  loadAnalytics: async () => {
    set((state) => ({ loading: { ...state.loading, analytics: true }, errors: { ...state.errors, analytics: undefined } }));
    try {
      const firmId = resolveFirmId(get, set);
      if (!firmId) {
        set((state) => ({
          loading: { ...state.loading, analytics: false },
          errors: { ...state.errors, analytics: 'CA firm context is missing. Re-login or switch workspace.' },
        }));
        return;
      }
      const analytics = await caService.getDashboardAnalytics({ firmId });
      set((state) => ({ analytics, loading: { ...state.loading, analytics: false } }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          analytics: null,
          loading: { ...state.loading, analytics: false },
          errors: { ...state.errors, analytics: undefined },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, analytics: false },
        errors: { ...state.errors, analytics: serviceError.message },
      }));
    }
  },

  createTask: async ({ clientOrganizationId, mappingId, title, description, dueDate, priority, assigneeUserId, ownerSide }) => {
    const { selectedClientOrgId, selectedMappingId, clients } = get();
    const resolvedClientOrgId = clientOrganizationId ?? selectedClientOrgId;
    const resolvedMappingId =
      mappingId ??
      selectedMappingId ??
      clients.find((client) => client.organization.id === resolvedClientOrgId)?.mapping.id;
    if (!resolvedClientOrgId || !resolvedMappingId) return;
    set((state) => ({ loading: { ...state.loading, createTask: true }, errors: { ...state.errors, createTask: undefined } }));
    try {
      await caService.createTask({
        clientOrganizationId: resolvedClientOrgId,
        mappingId: resolvedMappingId,
        title,
        ...(description ? { description } : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(priority ? { priority } : {}),
        ...(assigneeUserId ? { assigneeUserId } : {}),
        ...(ownerSide ? { ownerSide } : {}),
      });
      await get().loadTasks(resolvedClientOrgId, resolvedMappingId);
      set((state) => ({ loading: { ...state.loading, createTask: false } }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, createTask: false },
        errors: { ...state.errors, createTask: serviceError.message },
      }));
    }
  },
  createDocumentRequest: async ({ title, dueDate, notes }) => {
    const { selectedClientOrgId, selectedMappingId } = get();
    if (!selectedClientOrgId || !selectedMappingId) return;
    set((state) => ({
      loading: { ...state.loading, createDocumentRequest: true },
      errors: { ...state.errors, createDocumentRequest: undefined },
    }));
    try {
      await caService.createDocumentRequest({
        mappingId: selectedMappingId,
        clientOrganizationId: selectedClientOrgId,
        title,
        ...(dueDate ? { dueDate } : {}),
        ...(notes ? { notes } : {}),
      });
      await Promise.all([
        get().loadDocuments(selectedClientOrgId, selectedMappingId),
        get().loadDocumentWorkflow(selectedClientOrgId, selectedMappingId),
        get().loadAuditLog(selectedClientOrgId, selectedMappingId),
      ]);
      set((state) => ({ loading: { ...state.loading, createDocumentRequest: false } }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          loading: { ...state.loading, createDocumentRequest: false },
          errors: { ...state.errors, createDocumentRequest: 'Document request creation is not yet available on your backend.' },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, createDocumentRequest: false },
        errors: { ...state.errors, createDocumentRequest: serviceError.message },
      }));
    }
  },
  uploadDocument: async ({ documentRequestId, fileName, contentType }) => {
    const { selectedClientOrgId, selectedMappingId } = get();
    if (!selectedClientOrgId || !selectedMappingId) return;
    set((state) => ({
      loading: { ...state.loading, uploadDocument: true },
      errors: { ...state.errors, uploadDocument: undefined },
    }));
    try {
      const init = await caService.initDocumentUpload({
        mappingId: selectedMappingId,
        clientOrganizationId: selectedClientOrgId,
        documentRequestId,
        fileName,
        contentType,
      });
      await caService.completeDocumentUpload({
        mappingId: selectedMappingId,
        clientOrganizationId: selectedClientOrgId,
        documentRequestId,
        ...(init.uploadId ? { uploadId: init.uploadId } : {}),
        ...(init.objectKey ? { objectKey: init.objectKey } : {}),
      });
      await Promise.all([
        get().loadDocuments(selectedClientOrgId, selectedMappingId),
        get().loadDocumentWorkflow(selectedClientOrgId, selectedMappingId),
        get().loadAuditLog(selectedClientOrgId, selectedMappingId),
      ]);
      set((state) => ({ loading: { ...state.loading, uploadDocument: false } }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          loading: { ...state.loading, uploadDocument: false },
          errors: { ...state.errors, uploadDocument: 'Document upload is not yet available on your backend.' },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, uploadDocument: false },
        errors: { ...state.errors, uploadDocument: serviceError.message },
      }));
    }
  },

  updateClientStage: async ({ stage, pitchNotes }) => {
    const { selectedClientOrgId, selectedMappingId } = get();
    if (!selectedClientOrgId || !selectedMappingId) return;
    set((state) => ({ loading: { ...state.loading, updateStage: true }, errors: { ...state.errors, updateStage: undefined } }));
    try {
      await caService.updateClientStage({
        mappingId: selectedMappingId,
        clientOrganizationId: selectedClientOrgId,
        stage,
        ...(pitchNotes ? { pitchNotes } : {}),
      });
      await get().loadClients();
      await get().loadClientDetails(selectedClientOrgId, selectedMappingId);
      set((state) => ({ loading: { ...state.loading, updateStage: false } }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          loading: { ...state.loading, updateStage: false },
          errors: { ...state.errors, updateStage: 'Stage updates are not yet available on your backend.' },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, updateStage: false },
        errors: { ...state.errors, updateStage: serviceError.message },
      }));
    }
  },

  updateAssignment: async ({ assignmentId, assigneeUserId, dueDate, dependencyTaskIds }) => {
    const { selectedClientOrgId, selectedMappingId } = get();
    if (!selectedClientOrgId || !selectedMappingId) return;
    set((state) => ({ loading: { ...state.loading, updateAssignment: true }, errors: { ...state.errors, updateAssignment: undefined } }));
    try {
      await caService.updateAssignment({
        assignmentId,
        ...(assigneeUserId ? { assigneeUserId } : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(dependencyTaskIds ? { dependencyTaskIds } : {}),
      });
      await get().loadAssignments(selectedClientOrgId, selectedMappingId);
      set((state) => ({ loading: { ...state.loading, updateAssignment: false } }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          loading: { ...state.loading, updateAssignment: false },
          errors: { ...state.errors, updateAssignment: 'Assignment updates are not yet available on your backend.' },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, updateAssignment: false },
        errors: { ...state.errors, updateAssignment: serviceError.message },
      }));
    }
  },

  updateDocumentLifecycle: async ({ documentRequestId, status }) => {
    const { selectedClientOrgId, selectedMappingId } = get();
    if (!selectedClientOrgId || !selectedMappingId) return;
    set((state) => ({ loading: { ...state.loading, documentLifecycle: true }, errors: { ...state.errors, documentLifecycle: undefined } }));
    try {
      await caService.updateDocumentLifecycle({
        mappingId: selectedMappingId,
        clientOrganizationId: selectedClientOrgId,
        documentRequestId,
        status,
      });
      await Promise.all([
        get().loadDocuments(selectedClientOrgId, selectedMappingId),
        get().loadDocumentWorkflow(selectedClientOrgId, selectedMappingId),
        get().loadAuditLog(selectedClientOrgId, selectedMappingId),
      ]);
      set((state) => ({ loading: { ...state.loading, documentLifecycle: false } }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          loading: { ...state.loading, documentLifecycle: false },
          errors: { ...state.errors, documentLifecycle: 'Document lifecycle updates are not yet available on your backend.' },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, documentLifecycle: false },
        errors: { ...state.errors, documentLifecycle: serviceError.message },
      }));
    }
  },

  bulkDocumentAction: async ({ action, documentIds, status }) => {
    const { selectedClientOrgId, selectedMappingId } = get();
    if (!selectedClientOrgId || !selectedMappingId) return;
    set((state) => ({ loading: { ...state.loading, documentLifecycle: true }, errors: { ...state.errors, documentLifecycle: undefined } }));
    try {
      await caService.bulkDocumentAction({
        mappingId: selectedMappingId,
        clientOrganizationId: selectedClientOrgId,
        action,
        documentIds,
        ...(status ? { status } : {}),
      });
      await Promise.all([
        get().loadDocuments(selectedClientOrgId, selectedMappingId),
        get().loadDocumentWorkflow(selectedClientOrgId, selectedMappingId),
      ]);
      set((state) => ({ loading: { ...state.loading, documentLifecycle: false } }));
    } catch (error) {
      if (isFeatureUnavailableError(error)) {
        set((state) => ({
          loading: { ...state.loading, documentLifecycle: false },
          errors: { ...state.errors, documentLifecycle: 'Bulk document actions are not yet available on your backend.' },
        }));
        return;
      }
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, documentLifecycle: false },
        errors: { ...state.errors, documentLifecycle: serviceError.message },
      }));
    }
  },

  inviteClient: async ({ email, organizationName, accessLevel, inviteTarget }) => {
    set((state) => ({ loading: { ...state.loading, inviteClient: true }, errors: { ...state.errors, inviteClient: undefined } }));
    try {
      const firmId = resolveFirmId(get, set);
      if (!firmId) {
        set((state) => ({
          loading: { ...state.loading, inviteClient: false },
          errors: { ...state.errors, inviteClient: 'CA firm context is missing. Re-login or switch workspace.' },
        }));
        return;
      }
      await caService.inviteClient({
        firmId,
        email,
        accessLevel,
        ...(inviteTarget ? { inviteTarget } : {}),
        ...(organizationName ? { organizationName } : {}),
      });
      await get().loadClients();
      set((state) => ({ loading: { ...state.loading, inviteClient: false } }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, inviteClient: false },
        errors: { ...state.errors, inviteClient: serviceError.message },
      }));
    }
  },

  revokeClientInvite: async (inviteId: string) => {
    set((state) => ({
      loading: { ...state.loading, revokeClientInvite: true },
      errors: { ...state.errors, revokeClientInvite: undefined },
    }));
    try {
      await caService.revokeClientInvite({ inviteId });
      await get().loadClients();
      set((state) => ({ loading: { ...state.loading, revokeClientInvite: false } }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, revokeClientInvite: false },
        errors: { ...state.errors, revokeClientInvite: serviceError.message },
      }));
      throw serviceError;
    }
  },

  createClient: async ({ businessName, gstin, email, phone }) => {
    set((state) => ({ loading: { ...state.loading, createClient: true }, errors: { ...state.errors, createClient: undefined } }));
    try {
      const firmId = resolveFirmId(get, set);
      if (!firmId) {
        set((state) => ({
          loading: { ...state.loading, createClient: false },
          errors: { ...state.errors, createClient: 'CA firm context is missing. Re-login or switch workspace.' },
        }));
        return;
      }
      await caService.createClient({
        firmId,
        businessName,
        ...(gstin ? { gstin } : {}),
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
      });
      await get().loadClients();
      set((state) => ({ loading: { ...state.loading, createClient: false } }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, createClient: false },
        errors: { ...state.errors, createClient: serviceError.message },
      }));
    }
  },
  loadStaff: async () => {
    set((state) => ({ loading: { ...state.loading, staff: true }, errors: { ...state.errors, staff: undefined } }));
    try {
      const firmId = resolveFirmId(get, set);
      if (!firmId) {
        set((state) => ({
          loading: { ...state.loading, staff: false },
          errors: { ...state.errors, staff: 'CA firm context is missing. Re-login or switch workspace.' },
        }));
        return;
      }
      const response = await caService.getStaff({ firmId });
      set((state) => ({ staff: response.items, loading: { ...state.loading, staff: false } }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, staff: false },
        errors: { ...state.errors, staff: serviceError.message },
      }));
    }
  },
  createStaff: async ({ fullName, email, phone, role }) => {
    set((state) => ({ loading: { ...state.loading, staff: true }, errors: { ...state.errors, staff: undefined } }));
    try {
      const firmId = resolveFirmId(get, set);
      if (!firmId) {
        set((state) => ({
          loading: { ...state.loading, staff: false },
          errors: { ...state.errors, staff: 'CA firm context is missing. Re-login or switch workspace.' },
        }));
        return;
      }
      await caService.createStaff({ firmId, fullName, email, ...(phone ? { phone } : {}), role });
      await get().loadStaff();
      set((state) => ({ loading: { ...state.loading, staff: false } }));
    } catch (error) {
      const serviceError = asCAServiceError(error);
      set((state) => ({
        loading: { ...state.loading, staff: false },
        errors: { ...state.errors, staff: serviceError.message },
      }));
    }
  },
}));
